import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { SlotSection, addMin } from './SlotSection';
import { ClientSection, clientLabel } from './ClientSection';
import { NewClientSection } from './NewClientSection';
import { SummarySection } from './SummarySection';

const Step = ({ n, title, children }) => (
  <div className="space-y-3">
    <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider"><span className="w-6 h-6 rounded-full bg-[#FACC15] text-slate-900 flex items-center justify-center text-xs">{n}</span>{title}</h3>
    {children}
  </div>
);

const waAllowed = (c) => !c.do_not_contact && ['whatsapp', 'both', undefined, ''].includes(c.notification_preference);
const INITIAL_DETAILS = { motifs: [], precisions: [], client_request: '', return_time: '', client_waits: false, loaner_needed: false, assigned_employee_id: '' };
const FIELD_FR = {
  displacement_cc: 'cylindrée', power_kw: 'puissance', total_weight_kg: 'poids total', empty_weight_kg: 'poids à vide', payload_kg: 'charge utile',
  seats_total: 'places', seats_front: 'places avant', date: 'date', time: 'heure', duration_min: 'durée', phone: 'téléphone', email: 'e-mail',
};

const vehicleState = (cl, id) => {
  const v = cl?.vehicles?.find(x => x.id === id);
  return v ? { mode: 'existing', id: v.id, label: `${v.brand} ${v.model} · ${v.plate}` } : { mode: 'none' };
};

export const BookingPanel = ({ initial, onClose, onPreview, onBooked }) => {
  const edit = initial.edit;
  const appt = edit?.appointment;
  const [slot, setSlot] = useState({ date: initial.date, time: initial.time, duration: edit?.duration_min || 60 });
  const [avail, setAvail] = useState(null);
  const [client, setClient] = useState(edit?.client || null);
  const [newClient, setNewClient] = useState(null);
  const [creating, setCreating] = useState(null);
  const [vehicle, setVehicle] = useState(edit ? vehicleState(edit.client, appt.vehicle_id) : { mode: 'none' });
  const [details, setDetails] = useState(edit ? { ...INITIAL_DETAILS, ...edit.details } : INITIAL_DETAILS);
  const [billing, setBilling] = useState(null);
  const [sendWa, setSendWa] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({ motifs: [], internal_motifs: [], rdv_motifs: [] });
  const [kind, setKindRaw] = useState(edit?.details?.motifs?.some(m => m.startsWith('rdv_')) ? 'rdv' : 'atelier');
  useEffect(() => { axios.get(`${API}/workshop/catalog`).then(r => setCatalog(r.data)).catch(() => {}); }, []);
  const isInternal = !!client?.is_internal;
  const isRdv = !isInternal && kind === 'rdv';
  const setKind = (k) => { setKindRaw(k); setDetails(d => ({ ...d, motifs: [], precisions: [] })); };
  const motifs = isInternal ? catalog.internal_motifs || [] : isRdv ? catalog.rdv_motifs || [] : catalog.motifs;
  const prevInternal = useRef(isInternal);
  useEffect(() => {
    if (prevInternal.current === isInternal) return;
    prevInternal.current = isInternal;
    setDetails(d => ({ ...d, motifs: [], precisions: [] }));
  }, [isInternal]);

  const c = client || newClient?.client;
  const waNumber = c ? (c.whatsapp || c.phone || '') : '';
  useEffect(() => { if (!edit) setSendWa(!!c && !!waNumber && waAllowed(c)); }, [client, newClient]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onPreview(avail ? { ...avail, ok: avail.ok } : null); }, [avail]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (initial.nonce) setSlot(s => ({ ...s, date: initial.date, time: initial.time })); }, [initial.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onPreview(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  const vehicleLabel = newClient ? (newClient.vehicle?.plate || newClient.vehicle?.brand ? `${newClient.vehicle.brand} ${newClient.vehicle.model} · ${newClient.vehicle.plate}` : "À compléter à l'arrivée")
    : vehicle.mode === 'existing' ? vehicle.label : vehicle.mode === 'new' && (vehicle.data.plate || vehicle.data.brand) ? `${vehicle.data.brand} ${vehicle.data.model} · ${vehicle.data.plate} (nouveau)` : "À compléter à l'arrivée";
  const recap = {
    client: c ? clientLabel(c) : '', vehicle: isRdv ? (vehicle.mode === 'existing' ? vehicle.label : '—') : vehicleLabel, duration: slot.duration, bay: isRdv ? '' : avail?.bay,
    segments: isRdv ? (slot.time ? [{ start: slot.time, end: addMin(slot.time, slot.duration) }] : []) : avail?.segments || [],
    dateLabel: new Date(`${slot.date}T12:00:00`).toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
  };

  const useExisting = (cl, vehicleId) => {
    setNewClient(null); setCreating(null); setClient(cl);
    const v = cl.vehicles.find(x => x.id === vehicleId) || (cl.vehicles.length === 1 ? cl.vehicles[0] : null);
    setVehicle(v ? { mode: 'existing', id: v.id, label: `${v.brand} ${v.model} · ${v.plate}` } : { mode: 'none' });
  };

  const missing = [
    !c && (creating !== null ? 'cliquez sur « Continuer » sous la fiche du nouveau client (étape 1)' : 'le client (étape 1)'),
    !isRdv && (!avail ? 'la vérification du créneau (étape 2)' : !avail.ok && `un créneau libre (${avail.reason || 'créneau occupé'})`),
    !details.motifs.length && (isRdv ? 'le type de rendez-vous (étape 3)' : 'au moins un motif (étape 3)'),
  ].filter(Boolean);

  const save = async () => {
    if (!c) { toast.error('Sélectionnez ou créez un client'); return; }
    if (!isRdv && !avail?.ok) { toast.error('Le créneau n\'est pas disponible'); return; }
    if (!details.motifs.length) { toast.error('Choisissez au moins un motif'); return; }
    const nv = newClient ? ((newClient.vehicle?.plate || newClient.vehicle?.brand) ? newClient.vehicle : null) : (vehicle.mode === 'new' ? vehicle.data : null);
    setSaving(true);
    try {
      const r = await axios[edit ? 'put' : 'post'](edit ? `${API}/agenda/book/${appt.id}` : `${API}/agenda/book`, {
        date: slot.date, time: slot.time, duration_min: slot.duration,
        client_id: client?.id || '', new_client: newClient ? newClient.client : null, ignore_duplicates: !!newClient?.ignoreDuplicates,
        vehicle_id: !newClient && vehicle.mode === 'existing' ? vehicle.id : '', new_vehicle: nv,
        billing_client_id: billing?.id || appt?.billing_client_id || '', billing_type: appt?.billing_type || 'reparation', category: details.motifs.length === 1 && details.motifs[0] === 'carrosserie' ? 'carrosserie' : 'mecanique', ...details,
        send_whatsapp: sendWa,
      });
      const wa = r.data.whatsapp;
      const done = edit ? 'Rendez-vous modifié' : 'Rendez-vous enregistré';
      if (wa.status === 'sent') toast.success(`${done} · Message WhatsApp envoyé`);
      else if (wa.status === 'manual' && sendWa) { toast.warning(`${done} · WhatsApp à envoyer manuellement`); if (wa.whatsapp_url) window.open(wa.whatsapp_url, '_blank'); }
      else if (wa.status === 'failed' && sendWa) toast.warning(`${done} · Échec de l'envoi WhatsApp : ${wa.error || 'erreur inconnue'}`);
      else toast.success(done);
      onBooked(r.data.appointment);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? `Champ invalide : ${d.map(e => FIELD_FR[e.loc?.[e.loc.length - 1]] || e.loc?.[e.loc.length - 1]).join(', ')}` : (d?.message || 'Erreur lors de l\'enregistrement'), { duration: 8000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-0 sm:p-6 animate-in fade-in duration-150" data-testid="booking-overlay">
    <div className="bg-white w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" data-testid="booking-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b bg-[#0E1116] text-white">
        <h2 className="text-lg font-bold" data-testid="booking-title">{edit ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</h2>
        <button onClick={onClose} className="hover:text-[#FACC15]" data-testid="booking-close-btn"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
        <Step n="1" title="Rechercher ou créer le client">
          {newClient ? (
            <div className="p-3 rounded-xl bg-slate-50 border flex items-start gap-2" data-testid="booking-new-client-ready">
              <div className="flex-1 text-sm"><p className="font-semibold">{clientLabel(newClient.client)} <span className="text-xs font-normal text-emerald-700">· nouveau client</span></p><p className="text-xs text-slate-500">{vehicleLabel}</p></div>
              <button type="button" onClick={() => { setNewClient(null); setCreating({ q: '', mode: 'form' }); }} className="text-xs underline" data-testid="booking-new-client-edit">Modifier</button>
            </div>
          ) : creating !== null ? <NewClientSection initialQuery={creating.q} initialMode={creating.mode} onDone={(nc) => { setNewClient(nc); setCreating(null); }} onUseExisting={useExisting} onCancel={() => setCreating(null)} />
            : <ClientSection client={client} setClient={setClient} vehicle={vehicle} setVehicle={setVehicle} onCreateNew={(q, mode) => setCreating({ q, mode })} />}
        </Step>
        <Step n="2" title="Créneau et durée"><SlotSection slot={slot} setSlot={setSlot} onAvailability={setAvail} excludeId={appt?.id || ''} freeSlot={isRdv} /></Step>
        <Step n="3" title={isInternal ? 'Tâche interne du garage (non facturée)' : 'Type de rendez-vous et validation'}>
          {!isInternal && (
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="booking-kind">
              {[['atelier', 'Travaux atelier', 'avec véhicule, crée une fiche de travail'], ['rdv', 'Autre rendez-vous', 'courtoisie, voiture neuve… sans véhicule ni travaux']].map(([k, l, d]) => (
                <button key={k} type="button" onClick={() => setKind(k)} data-testid={`booking-kind-${k}`}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${kind === k ? 'border-slate-900 bg-[#FACC15]/25' : 'border-slate-200 hover:border-slate-400'}`}>
                  <p className="font-bold text-sm">{l}</p><p className="text-xs text-slate-500">{d}</p>
                </button>))}
            </div>
          )}
          {isRdv && <p className="text-xs text-slate-500 mb-3" data-testid="booking-rdv-hint">Ce rendez-vous ne bloque pas l'atelier : il peut être pris même si le créneau atelier est occupé.</p>}
          <SummarySection motifs={motifs} details={details} setDetails={setDetails} recap={recap} billing={billing} setBilling={setBilling} sendWa={sendWa} setSendWa={setSendWa} waNumber={waNumber} isRdv={isRdv} />
        </Step>
      </div>
      <div className="p-4 border-t space-y-2">
        {missing.length > 0 && !saving && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="booking-missing">
            <b>Pour enregistrer, il manque :</b> {missing.join(' · ')}
          </p>
        )}
        <Button className="w-full h-12 text-base bg-[#0E1116] hover:bg-black text-[#FACC15]" disabled={saving || missing.length > 0} onClick={save} data-testid="booking-save-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : edit ? 'Enregistrer les modifications' : 'Enregistrer le rendez-vous'}
        </Button>
      </div>
    </div>
    </div>
  );
};
